<template>
  <div class="mb-8 mt-4">
    <o-field-group
      v-slot="{ status }"
      class="w-full"
      :label="_field.name"
      :message="_field.information"
      :rules="rules"
    >
      <component
        :is="component"
        :key="_field.id"
        v-model="_value"
        v-bind="componentProps"
        :options="options"
        :status="status"
      />
    </o-field-group>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import * as Fields from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  OFieldGroup,
  OAnnotate,
} from '@/components/elements'

import { FormItemDataType } from '~/_api/models/formItem'

interface Props {
  field: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:field', value: Record<string, any>): void
}>()

const { t } = useI18n()

const _field = computed({
  get () {
    return props.field
  },
  set (value) {
    emit('update:field', value)
  },
})

const _value = computed({
  get () {
    if (isMultiField.value || isMultiSelectField.value) {
      return _field.value.value || []
    }

    return props.field.value
  },
  set (value) {
    _field.value.value = value
  },
})

const isMultiField = computed(() => {
  return _field.value.dataType === FormItemDataType.multiple
})

const isMultiSelectField = computed(() => {
  return _field.value.dataType === FormItemDataType.multiSelect
})

const isYesNoField = computed(() => {
  return _field.value.dataType === FormItemDataType.yesNo
})

const options = computed(() => {
  if ((isMultiField.value || isMultiSelectField.value) && props.field.multipleValues.length) {
    return _field.value.multipleValues.map((value: any) => {
      return {
        value,
        text: value,
      }
    })
  }

  if (isYesNoField.value) {
    return [
      { value: true, text: t('general.yes') },
      { value: false, text: t('general.no') },
    ]
  }

  return []
})

const rules = computed(() => {
  const r: string[] = []

  if (_field.value.required) {
    r.push('required')
  }

  return r.join('|')
})

const component = computed(() => {
  switch (_field.value.dataType) {
    case FormItemDataType.text:
      return Fields.TextField
    case FormItemDataType.longtext:
      return Fields.TextAreaField
    case FormItemDataType.multiple:
      if (props.field.isDisplayList) {
        return Fields.SelectField
      }
      if (props.field.isDisplayRadio) {
        return Fields.RadioGroupField
      }
    // falls through
    case FormItemDataType.bool:
      return Fields.ToggleField
    case FormItemDataType.yesNo:
      return Fields.RadioGroupField
    case FormItemDataType.number:
      return Fields.NumberField
    case FormItemDataType.date:
      return Fields.DateField
    case FormItemDataType.signature:
      return Fields.SignatureField
    case FormItemDataType.annotation:
      return OAnnotate
    case FormItemDataType.multiSelect:
      return Fields.CheckboxGroupField
    default:
      return Fields.TextField
  }
})

const componentProps = computed(() => {
  switch (_field.value.dataType) {
    case FormItemDataType.longtext:
      return {
        autoresize: true,
      }
    case FormItemDataType.multiSelect:
      return {
        class: '-mx-4',
      }
    case FormItemDataType.date:
      return {
        augmenter: Augmenters.timestamp,
      }
    case FormItemDataType.annotation:
      return {
        image: _field.value.images?.large,
        config: _field.value.options,
        portal: 'modal-container',
        label: _field.value.name,
      }
    default:
  }

  return null
})
</script>
